import { Alert, Anchor, Center, Group, Loader, PinInput, Stack, Text, Title } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Panel } from '../../../components/global/Panel/Panel.tsx';
import { CenterLayout } from '../../../components/layout/CenterLayout.tsx';
import { queryKeysFactory } from '../../../lib/queryKeysFactory.ts';
import { auth } from '../../../services/directusClient/directusClient.ts';
import { resendVerificationCode, verifyAccount } from '../../../services/promoterAuthentication.ts';
import { tanstackQueryClient } from '../../../services/tanstackQueryClient.ts';

const errorMessages = {
  VALIDATION_ERROR: 'Please ensure all fields are filled out correctly.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again later.',
  CODE_NOT_CORRECT: 'The code you entered is incorrect. Please check and try again.',
  CODE_EXPIRED: 'The code has expired. Please request a new one.',
} as const;

export const VerifyEmail: FC = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const { mutate: resendVerificationCodeMutation, isPending: isResendingCode } = useMutation({
    mutationFn: async (email: string) => {
      await resendVerificationCode({ email });
    },
  });

  const { mutate: verifyEmailCodeMutation, isPending: isVerifyingCode } = useMutation({
    mutationFn: async ({ code }: { code: string }) => verifyAccount({ verificationCode: code, userId: userId ?? '' }),
    onSuccess: async (response) => {
      setErrorMessage(undefined);

      if ('success' in response && response.success) {
        localStorage.removeItem('tbrem');

        // The server logs the promoter in as soon as the code is right.
        if ('data' in response && response.data) {
          auth.setSession(response.data);
          await tanstackQueryClient.invalidateQueries({ queryKey: queryKeysFactory('user').detail('me') });
          void navigate('/dashboard');
          return;
        }

        void navigate('/auth/login');
        return;
      }

      if ('type' in response) {
        setErrorMessage(errorMessages[response.type]);
      }
    },
  });

  const handleResendVerificationCode = () => {
    const localEmail = localStorage.getItem('tbrem');

    if (!localEmail) {
      void navigate('/auth/signup');
      return;
    }

    resendVerificationCodeMutation(localEmail);
  };

  if (!userId) {
    void navigate('/auth/login');
    return;
  }

  return (
    <CenterLayout>
      <Panel>
        <Title order={2} ta="center">
          Email Verification
        </Title>
        <Text c="gigi-grey-text" size="sm" ta="center">
          A verification email has been sent to your email address. Please check your inbox and follow the instructions
          to verify your email. If you didn’t receive the email, check your spam or junk folder.
        </Text>
        <Stack align="center" gap="xxxs">
          <Text size="xs">Enter the 6-character code</Text>
          <PinInput
            disabled={isResendingCode || isVerifyingCode}
            length={6}
            onComplete={(code) => verifyEmailCodeMutation({ code: code.toUpperCase() })}
          />
        </Stack>
        {(isResendingCode || isVerifyingCode) && (
          <Center>
            <Loader color="grape" />
          </Center>
        )}
        {errorMessage && (
          <Alert color="gigi-apple-pink" p="xs" radius="xs">
            {errorMessage}
          </Alert>
        )}
        <Group gap="xxxs" justify="center" mt="xl">
          <Text c="gigi-grey-text" size="xs">
            Didn&apos;t receive the email?
          </Text>
          <Anchor size="xs" onClick={handleResendVerificationCode}>
            Send again
          </Anchor>
        </Group>
      </Panel>
    </CenterLayout>
  );
};
